import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import {
  CUSTOM_FILTER_CHIP_ID,
  type IFilterData,
  type IQuickFilter,
} from "../../components/Organismos/Table/FilteredTable.types";
import { getEvents } from "../../Services/Event";
import { getEmailCategories } from "../../Services/EmailCategory";
import {
  createEmailTemplate,
  deleteEmailTemplate,
  checkTemplateLinkedToScheduled,
  fetchEmailTemplateTitlesLowercaseSet,
  getEmailTemplateByIdForDuplicate,
} from "../../Services/EmailTemplate";
import { IEmailCategory } from "../../interfaces/models/IEmailCategory";
import { IEmailTemplate } from "../../interfaces/models/IEmailTemplate";
import { EmailType, EmailTypeEnum } from "../../Enums/EmailEnum";
import { AccessTypeWithStaff } from "../../Enums/UserEnums";
import { formatDateToString } from "../../utils/functions";
import TemplateDateFlightsPicker from "../SendEmail/TemplateDateFlightsPicker";
import {
  buildTemplateCreatedDateFilter,
  summarizeTemplateDateFilterSelection,
} from "../SendEmail/templateDateFilter";
import {
  DEFAULT_TEMPLATE_DATE_FILTER,
  type TemplateDateFilterValue,
} from "../../utils/templateDateFilterTypes";
import { nextDuplicateTemplateTitle } from "../../utils/emailTemplateDuplicateTitle";
import { toastError, toastSuccess } from "../../utils/toast";
import NewButton from "../../components/Atomos/Buttons/newButton";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import Email from "../../components/Atomos/Icons/Email";
import { useAuth } from "../../store/AuthContext";
import GrayEye from "../../components/Atomos/Icons/grayEye";
import EditEnvelope from "../../components/Atomos/Icons/EditEnvelope";
import EditDoc from "../../components/Atomos/Icons/EditDoc";
import Log from "../../components/Atomos/Icons/LogOutlined";
import Delete from "../../components/Atomos/Icons/Delete";
import Copy from "../../components/Atomos/Icons/Copy";
import Button from "../../components/staff/Button";

const endpoint = "/emailTemplate";

function templateDateFlightFilterChips (filterValue: unknown) {
  const label = summarizeTemplateDateFilterSelection(
    filterValue as TemplateDateFilterValue | null | undefined,
  );
  if (!label) {
    return [];
  }
  return [{ id: CUSTOM_FILTER_CHIP_ID, name: label }];
}

function ActionTooltip ({ text, children }: { text: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="relative group inline-flex">
      {children}
      <div className="absolute right-0 top-full mt-2 hidden group-hover:block group-focus-within:block z-50 pointer-events-none">
        <div className="relative rounded bg-gray-font px-3 py-2 text-sm text-neutral shadow-lg whitespace-nowrap">
          {text}
          <div className="absolute right-3 bottom-full h-0 w-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-font" />
        </div>
      </div>
    </div>
  );
}

export default function EmailDesign (): React.ReactElement {
  const [emailCategories, setEmailCategories] = useState<IEmailCategory[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; emailNumber?: string; titleFormatted?: string } | null>(null);
  const [deleteModalMode, setDeleteModalMode] = useState<"confirm" | "cannot" | null>(null);
  const [checkingScheduled, setCheckingScheduled] = useState(false);
  const navigate = useNavigate();

  const { tokenDecoded } = useAuth();
  const isSuperStaff = tokenDecoded?.extension_WSG_Permissions === AccessTypeWithStaff.wsgSuper;

  const openDeleteModal = useCallback(async (row: { id: string; emailNumber?: string; titleFormatted?: string }) => {
    if (!row?.id) {
      return;
    }
    setCheckingScheduled(true);
    try {
      const linked = await checkTemplateLinkedToScheduled(row.id);
      setDeleteTarget({ id: row.id, emailNumber: row.emailNumber, titleFormatted: row.titleFormatted });
      setDeleteModalMode(linked ? "cannot" : "confirm");
      setDeleteModalOpen(true);
    } finally {
      setCheckingScheduled(false);
    }
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setDeleteModalMode(null);
  }, []);

  const handleViewScheduledEmail = useCallback(() => {
    closeDeleteModal();
    navigate("/send-email/scheduled-emails");
  }, [closeDeleteModal, navigate]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget?.id) {
      return;
    }
    setDeletingId(deleteTarget.id);
    try {
      await deleteEmailTemplate(deleteTarget.id);
      closeDeleteModal();
      window.location.reload();
    } finally {
      setDeletingId(null);
    }
  }, [deleteTarget, closeDeleteModal]);

  const handleDuplicateTemplate = useCallback(
    async (row: { id?: string }) => {
      if (!row?.id) {
        return;
      }
      setDuplicatingId(row.id);
      try {
        const [source, titlesLower] = await Promise.all([
          getEmailTemplateByIdForDuplicate(row.id),
          fetchEmailTemplateTitlesLowercaseSet(),
        ]);
        if (!source) {
          toastError("Could not load the template to duplicate.");
          return;
        }
        const baseTitle = source.title ?? "";
        const buildPayload = (title: string): IEmailTemplate => ({
          title,
          category: source.category ?? "",
          subject: source.subject ?? "",
          sender: source.sender ?? "",
          fromEmail: source.fromEmail ?? "",
          emailType: source.emailType,
          attachment: source.attachment ?? "",
          template: source.template ?? "",
          templateSource: source.templateSource ?? "beefree",
          emailEventId: source.emailEventId,
          beefreeJson: source.beefreeJson,
          createdDate: Math.floor(Date.now() / 1000),
          isDeleted: false,
        });

        let titlesForName = titlesLower;
        let newTitle = nextDuplicateTemplateTitle(baseTitle, titlesForName);
        const tryCreate = async (title: string) => createEmailTemplate(buildPayload(title));

        const maxAttempts = 5;
        for (let attempt = 0;attempt < maxAttempts;attempt++) {
          try {
            await tryCreate(newTitle);
            break;
          } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 409 && attempt < maxAttempts - 1) {
              titlesForName.add(newTitle.toLowerCase());
              const refreshed = await fetchEmailTemplateTitlesLowercaseSet();
              for (const t of titlesForName) {
                refreshed.add(t);
              }
              titlesForName = refreshed;
              newTitle = nextDuplicateTemplateTitle(baseTitle, titlesForName);
              continue;
            }
            throw err;
          }
        }

        toastSuccess("Template duplicated.");
        window.location.reload();
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: unknown } })?.response?.data;
        const detail =
          typeof message === "string" && message.trim()
            ? message
            : "Could not duplicate the template. Try again.";
        toastError(detail);
      } finally {
        setDuplicatingId(null);
      }
    },
    [],
  );

  const getTemplateEditorPath = useCallback((row: Pick<IEmailTemplate, "id" | "templateSource"> | null | undefined) => {
    if (!row?.id) {
      return "/email-design";
    }
    return row.templateSource === "html"
      ? `/email-design/${row.id}/edit-html`
      : `/email-design/${row.id}/edit`;
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await getEmailCategories();
        setEmailCategories(Array.isArray(categories) ? categories : []);
      } catch {
        setEmailCategories([]);
      }
    };
    loadCategories();
  }, []);

  const emailTypeFilterOptions = useMemo(
    () =>
      (Object.entries(EmailTypeEnum) as [string, string][]).map(([key, label]) => ({
        id: key,
        name: label,
      })),
    [],
  );

  const categoryFilterOptions = useMemo(
    () =>
      emailCategories.map((c) => ({
        id: c.id ?? c.name,
        name: c.name,
      })),
    [emailCategories],
  );

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        name: "Date Created",
        fieldName: "createdDate",
        type: "custom",
        defaultCustomFilterValue: DEFAULT_TEMPLATE_DATE_FILTER,
        getCustomSelectedChips: templateDateFlightFilterChips,
        render: ({ value, apply }) => (
          <TemplateDateFlightsPicker
            value={
              (value as TemplateDateFilterValue | null | undefined) ??
              DEFAULT_TEMPLATE_DATE_FILTER
            }
            onApply={(next) => {
              const built = buildTemplateCreatedDateFilter(
                next.templateDatePreset,
                next.templateDateFrom,
                next.templateDateTo,
              );
              apply(next, built?.createdDate ?? null);
            }}
            variant="catalog"
            label="By Date Created:"
            dialogTitle="Date created"
            labelClassName="font-semibold text-gray-font"
            triggerClassName="rounded-md border border-solid !border-gray-300 !bg-gray-25 !shadow-none !hover:shadow-md"
            triggerInsetClassName="max-h-[0.875rem]"
          />
        ),
      },
      {
        name: "Category",
        fieldName: "category",
        type: "multiselect",
        objectData: categoryFilterOptions,
        comparison: "BY_NAME",
      },
      {
        name: "Type",
        fieldName: "emailType",
        type: "multiselect",
        objectData: emailTypeFilterOptions,
        comparison: "EQUAL",
      },
    ],
    [categoryFilterOptions, emailTypeFilterOptions],
  );

  const createdDateToDate = useCallback((createdDate: number | undefined): Date | null => {
    if (createdDate == null || createdDate === 0) {
      return null;
    }
    const ms = createdDate > 1e12 ? createdDate : createdDate * 1000;
    return new Date(ms);
  }, []);

  const formatData = useCallback((rows: IEmailTemplate[] | any[]) => {
    if (!rows || !Array.isArray(rows)) {
      return [];
    }
    return rows.map((row) => {
      const date = createdDateToDate(row.createdDate);
      const categoryFormatted = row.category ?? "";
      const titleFormatted = row.title ?? "";
      const createdDateStr = date ? formatDateToString(date) : "";
      const emailTypeLabel = EmailTypeEnum[row.emailType as EmailType] ?? row.emailType ?? "";
      const emailNumberStr = row.emailNumber ?? "";
      return {
        ...row,
        categoryFormatted,
        titleFormatted,
        emailNumber: emailNumberStr,
        emailType: emailTypeLabel,
        createdDate: createdDateStr,
      };
    });
  }, [createdDateToDate]);

  const columnsDef = useMemo(
    () => [
      {
        id: "title",
        header: "Category - Title",
        cell: ({ row }: { row: any }) => (
          <div className="flex flex-col gap-0.5">
            <span>
              <strong>{row.categoryFormatted}</strong>
              {row.categoryFormatted && row.titleFormatted ? " - " : ""}
              {row.titleFormatted}
            </span>
          </div>
        ),
      },
      {
        id: "emailNumber",
        header: "Email #",
        width: 104,
        cell: ({ row }: { row: any }) => <span>{row.emailNumber}</span>,
      },
      {
        id: "emailType",
        header: "Type",
        cell: ({ row }: { row: any }) => <span>{row.emailType}</span>,
      },
      {
        id: "createdDate",
        header: "Date Created",
        width: 140,
        cell: ({ row }: { row: any }) => <span>{row.createdDate}</span>,
      },
      {
        id: "actions",
        header: "Actions",
        width: 228,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const isDeleting = row?.id && deletingId === row.id;
          const isDuplicating = row?.id && duplicatingId === row.id;
          const canManageTemplate = !row?.isSystemTemplate || isSuperStaff;
          return (
            <div className="flex items-center justify-end gap-1 pr-1" role="group" aria-label="Actions">
              {row?.id && (
                <ActionTooltip text="View email information">
                  <Link
                    to={`/email-design/${row.id}`}
                    className="inline-flex w-8 h-8 items-center justify-center text-xs font-gray-font hover:text-primary"
                    aria-label="View email information"
                  >
                    <GrayEye />
                  </Link>
                </ActionTooltip>
              )}
              {row?.id && canManageTemplate && (
                <ActionTooltip text="Edit envelope">
                  <Link
                    to={`/email-design/${row.id}/edit-envelope`}
                    className="inline-flex w-8 h-8 items-center justify-center text-xs font-gray-font hover:text-primary"
                    aria-label="Edit envelope"
                  >
                    <EditEnvelope />
                  </Link>
                </ActionTooltip>
              )}
              {row?.id && canManageTemplate && (
                <ActionTooltip text="Edit template">
                  <Link
                    to={getTemplateEditorPath(row)}
                    className="inline-flex w-8 h-8 items-center justify-center text-xs font-gray-font hover:text-primary"
                    aria-label="Edit template"
                  >
                    <EditDoc />
                  </Link>
                </ActionTooltip>
              )}
              {row?.id && (
                <ActionTooltip text="View email logs">
                  <Link
                    to={`/email-design/${row.id}/logs`}
                    className="inline-flex w-8 h-8 items-center justify-center text-xs font-gray-font hover:text-primary"
                    aria-label="View email logs"
                  >
                    <Log />
                  </Link>
                </ActionTooltip>
              )}
              {row?.id && (
                <ActionTooltip text="Duplicate template">
                  <button
                    type="button"
                    onClick={() => handleDuplicateTemplate(row)}
                    disabled={!!duplicatingId}
                    className="inline-flex w-8 h-8 items-center justify-center text-xs font-gray-font hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Duplicate template"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </ActionTooltip>
              )}
              {row?.id && (
                <ActionTooltip
                  text={
                    canManageTemplate && isSuperStaff
                      ? "Delete template"
                      : row?.isSystemTemplate
                        ? "Only WSG Staff Super can modify system templates"
                        : "Only WSG Staff Super can delete templates"
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      canManageTemplate && isSuperStaff && openDeleteModal(row)
                    }
                    disabled={
                      isDeleting ||
                      isDuplicating ||
                      !isSuperStaff ||
                      !canManageTemplate ||
                      checkingScheduled
                    }
                    className="inline-flex w-8 h-8 items-center justify-center text-xs font-gray-font hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={isSuperStaff ? "Delete template" : "Delete template (WSG Staff Super only)"}
                  >
                    <Delete />
                  </button>
                </ActionTooltip>
              )}
            </div>
          );
        },
      },
    ],
    [
      deletingId,
      duplicatingId,
      openDeleteModal,
      isSuperStaff,
      checkingScheduled,
      getTemplateEditorPath,
      handleDuplicateTemplate,
    ],
  );

  const deleteConfirmMessage = deleteTarget
    ? (`Are you sure you want to delete this template ${deleteTarget.emailNumber ? `Email #${deleteTarget.emailNumber}` : ""}${deleteTarget.emailNumber && deleteTarget.titleFormatted ? " " : ""}${deleteTarget.titleFormatted ?? ""}`.trim() ||
      "Are you sure you want to delete this template?")
    : "Are you sure you want to delete this template?";

  const quickFilters: IQuickFilter[] = useMemo(
    () => [
      {
        key: "beefree",
        label: "Drag & Drop",
        hiddenQuery: {
          templateSource: {
            value: "beefree",
            comparisonType: "EQUAL",
          },
        },
      },
      {
        key: "html",
        label: "HTML Editor",
        hiddenQuery: {
          templateSource: {
            value: "html",
            comparisonType: "EQUAL",
          },
        },
      },
    ],
    [],
  );

  const newTemplateButtons = useMemo(
    () => (
      <div className="flex flex-wrap items-center justify-end gap-2 w-full md:w-auto ml-auto">
        <Link
          to="/email-design/add"
          state={{ templateSource: "beefree" }}
          className="w-full min-w-0 md:w-auto"
        >
          <Button
            text="New email"
            onClick={() => navigate("/email-design/add")}
            color="secondary"
            variant="filled"
            size="fixed"
            className="w-full md:w-auto justify-center"
            icon={<Email className="w-5 h-5" />}
          />
        </Link>
        <Link
          to="/email-design/add"
          state={{ templateSource: "html" }}
          className="w-full min-w-0 md:w-auto"
        >
          <Button
            text="New email - html"
            onClick={() => navigate("/email-design/add")}
            color="secondary"
            variant="filled"
            size="fixed"
            className="w-full md:w-auto justify-center"
            icon={<Email className="w-5 h-5" />}
          />
        </Link>
      </div>
    ),
    [],
  );

  return (
    <>
      {deleteModalOpen && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-template-modal-title"
          onClick={closeDeleteModal}
        >
          <div className="fixed inset-0 bg-black bg-opacity-80" />

          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                id="delete-template-modal-title"
                className="text-xl font-decimal font-medium text-Default mb-4"
              >
                {deleteModalMode === "cannot" ? "Cannot Delete Template" : "Delete Template"}
              </h2>

              {deleteModalMode === "cannot" ? (
                <>
                  <p className="text-gray-font mb-6">
                    This template is currently linked to a scheduled email. To delete this template, you must first cancel the scheduled mailing.
                  </p>
                  <div className="flex justify-end gap-4">
                    <LegacyButton
                      text="GOT IT"
                      style="outlined"
                      color="accent"
                      content="text"
                      size="small"
                      onClick={closeDeleteModal}
                    />
                    <LegacyButton
                      text="VIEW SCHEDULED EMAIL"
                      style="filled"
                      color="accent"
                      content="text"
                      size="small"
                      onClick={handleViewScheduledEmail}
                    />
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-font mb-6">{deleteConfirmMessage}</p>
                  <div className="flex justify-end gap-4">
                    <LegacyButton
                      text="NO, CANCEL"
                      style="outlined"
                      color="accent"
                      content="text"
                      size="small"
                      onClick={closeDeleteModal}
                    />
                    <LegacyButton
                      text={deletingId ? "DELETING..." : "YES, DELETE"}
                      style="filled"
                      color="accent"
                      content="text"
                      size="small"
                      onClick={handleConfirmDelete}
                      disabled={!!deletingId}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <FilteredTableCore
        columnsDef={columnsDef}
        showRowNumber
        endpoint={endpoint}
        filtersData={filtersData}
        formatData={formatData}
        quickFilters={quickFilters}
        quickFilterRequireSelection
        quickFilterBarPosition="belowSearch"
        persistFilters={false}
        persistSorting={false}
        defaultSorting={{ field: "emailNumber", order: "desc" }}
        showSearchInput
        searchPlaceholder="Search by title, category, or email number"
        quickFilterBarAccessory={newTemplateButtons}
        showExcelExport={false}
        showColumnVisibility={false}
      />
    </>
  );
}
